import { useEffect } from 'react';
import { useGetRegion } from '../../../hooks/useGetRegion';
import { useDispatch, useSelector } from 'react-redux';
import { countryRegionId } from '../../../redux/country-selectors';
import { Formik } from 'formik';
import { schema } from '../schema';
import { useIntl } from 'react-intl';
import ButtonComponent from '../../../../../../shared/components/button';
import MainUtils from '../../../../../../shared/utils/main';
import LanguagesTab from '../../../../../../shared/components/languages-tab';
import LangComponent from '../../country-info/lang-component';
import LanguageCode from '../../../../../../shared/services/types/language-code';
import { Skeleton, Row, Col } from 'antd';
import { Switch, Input } from 'formik-antd';
import { IoEarthOutline } from 'react-icons/io5';
import IntlMessages from '../../../../../../domain/utility/IntlMessages';
import FormItem from '../../../../../../shared/components/form-item';
import { useEditRegion } from '../../../hooks/useEditRegion';
import { useParams } from 'react-router-dom';
import { useCreateRegion } from '../../../hooks/useCreateRegion';
import {
  openRegionModal,
  setCountryRegionId,
  setRegionTableLoading,
} from '../../../redux/countrySlice';
import { validationInputNumber } from 'src/shared/functions/validation-input-number';
import { useLocaleContext } from 'src/domain/utility/AppContextProvider/LocaleContextProvide';
import { getLocaleValue } from 'src/shared/functions/change-locales';

function RegionForm() {
  const { mutate, data, isLoading: getLoading } = useGetRegion();
  const { mutate: create, isLoading: createLoading } = useCreateRegion();
  const { mutate: edit, isLoading: editLoading } = useEditRegion();
  const { id } = useParams();
  const regionId = useSelector(countryRegionId);
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const { locale }: any = useLocaleContext();
  useEffect(() => {
    if (regionId) mutate(regionId);
  }, [regionId]);

  useEffect(() => {
    dispatch(setRegionTableLoading(editLoading || createLoading));
  }, [editLoading, createLoading]);

  const Language = () => [
    {
      code: LanguageCode.English,
      langComponent: <LangComponent code={LanguageCode.English} index={0} />,
    },
    {
      code: LanguageCode.Español,
      langComponent: <LangComponent code={LanguageCode.Español} index={1} />,
    },
    {
      code: LanguageCode.Arabic,
      langComponent: <LangComponent code={LanguageCode.Arabic} index={2} />,
    },
  ];

  const handleSubmit = (values: any) => {
    if (regionId) {
      const newValues = {
        ...values,
        id: regionId,
        countryId: id,
        name: !MainUtils.isEmptyValue(
          getLocaleValue(values?.locales, LanguageCode.English, 'name'),
        )
          ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
          : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
      };
      edit(newValues);
      dispatch(setCountryRegionId(null));
    } else {
      const newValues = { ...values, countryId: id };
      create(newValues);
      dispatch(openRegionModal());
    }
  };

  return (
    <Formik
      initialValues={{
        locales:
          data?.Locales?.map((item) => {
            return {
              id: item.Id,
              languageId: item.LanguageId,
              localeKey: item.LocaleKey,
              localeValue: item.LocaleValue,
            };
          }) ?? [],
        name: data?.Name ?? '',
        published: data?.Published ?? false,
        displayOrder: data?.DisplayOrder ?? 0,
      }}
      validationSchema={schema(messages)}
      enableReinitialize
      onSubmit={(values) => handleSubmit(values)}>
      {(formik) => (
        <Row gutter={[0, 25]}>
          <Skeleton active loading={getLoading}>
            <Col span={24}>
              <Row justify='space-between' className='fullContent'>
                <Col>
                  <div className='text-title'>
                    <span className='icon-title'>
                      <IoEarthOutline />
                    </span>
                    {MainUtils.isEmptyValue(regionId)
                      ? messages['countries.region.add'].toString()
                      : `${messages['countries.region.edit'].toString()} - ${data?.Locales?.find(
                        (item) => item.LanguageId === locale.code,
                      )?.LocaleValue ?? data?.Name
                      }`}
                  </div>
                </Col>
                <Col>
                  <ButtonComponent
                    onClickAction={formik.handleSubmit}
                    type='primary'
                    loading={createLoading || editLoading}
                    text={
                      MainUtils.isEmptyValue(regionId)
                        ? messages['countries.region.add'].toString()
                        : messages['countries.region.edit'].toString()
                    }
                    size='small'
                    className='add-button'
                  />
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <div className='languages-tab'>
                <LanguagesTab
                  languagesList={Language()}
                  tabItem={{
                    key: 'Standard',
                    label: `${messages[
                      'category.categoryInfo.Standard'
                    ].toString()}`,
                    children: (
                      <LangComponent
                        code={`${!MainUtils.isEmptyValue(locale.code)
                          ? locale.code
                          : LanguageCode.English
                          }`}
                      />
                    ),
                  }}
                />
              </div>
            </Col>
            <Col span={24}>
              <FormItem
                name='published'
                label={messages['countries.Published'].toString()}
                fieldCommponent={
                  <Switch
                    name='published'
                    checkedChildren={<IntlMessages id='common.active' />}
                    unCheckedChildren={<IntlMessages id='common.inactive' />}
                  />
                }
              />
            </Col>
            <Col span={24}>
              <FormItem
                name='displayOrder'
                label={messages['countries.DisplayOrder'].toString()}
                fieldCommponent={
                  <Input
                    style={{ width: '100%' }}
                    defaultValue={0}
                    name='displayOrder'
                    onChange={(e) => {
                      if (validationInputNumber(e.target.value)) {
                        formik.setFieldValue(
                          'displayOrder',
                          Number(e.target.value),
                        );
                      } else formik.setFieldValue('displayOrder', 0);
                    }}
                  />
                }
              />
            </Col>
          </Skeleton>
        </Row>
      )}
    </Formik>
  );
}

export default RegionForm;
